import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Sparkles, ArrowRight, MapPin, Briefcase, SlidersHorizontal } from "lucide-react";
import { FUND_MODEL_LABEL, SEARCH_STAGE_LABEL } from "../../lib/taxonomy";
import { MatchScore, BreakdownChips, SEARCHER_HARD_MAX } from "../../components/MatchBadge";
import { SkeletonGrid } from "../../components/Skeleton";
import { t } from "../../lib/i18n";

export default function SearcherDashboard() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [welcome, setWelcome] = useState("");
  const [welcomeCta, setWelcomeCta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState(null); // null = loading

  useEffect(() => {
    (async () => {
      const r = await api.get("/marketplace");
      setCompanies(r.data || []);
      setLoading(false);
      api.get("/matches/searcher")
        .then((m) => setMatches(m.data || []))
        .catch(() => setMatches([]));
      try {
        const w = await api.post("/ai/welcome");
        setWelcome(w.data.message);
        setWelcomeCta(w.data?.cta_path ? { path: w.data.cta_path, label: w.data.cta_label } : null);
      } catch {}
    })();
  }, []);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 space-y-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-9 fade-up">
            <span className="font-mono-label text-gold">{t("Marketplace · Searchers")}</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
              {t("Empresas cualificadas,")}{" "}{user?.name?.split(" ")[0]}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              {t("Estas son las empresas pre-cualificadas disponibles. Expresa tu interés para iniciar el proceso (NDA, IOI, LOI, DD).")}
            </p>
          </div>
          <div className="lg:col-span-3 fade-up-delay-1">
            <div className="hairline px-5 py-4 bg-bone">
              <div className="font-mono-label text-muted-foreground">{t("Disponibles")}</div>
              <div className="font-serif-display text-4xl">{companies.length}</div>
            </div>
          </div>
        </div>

        {user?.searcher_profile && (
          <div className="hairline px-6 py-4 bg-card flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="searcher-profile-band">
            <span className="font-mono-label text-gold flex items-center gap-2">
              <SlidersHorizontal className="w-3 h-3" />{" "}{t("Tu búsqueda")}
            </span>
            <span className="text-sm">
              {(user.searcher_profile.sectors_interest || []).slice(0, 3).join(" · ")}
              {(user.searcher_profile.sectors_interest || []).length > 3 && " +"}
            </span>
            {user.searcher_profile.ebitda_min_eur != null && user.searcher_profile.ebitda_max_eur != null && (
              <span className="text-sm text-muted-foreground">
                {t("EBITDA")}{" "}{formatN(user.searcher_profile.ebitda_min_eur)}–{formatN(user.searcher_profile.ebitda_max_eur)} €
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {FUND_MODEL_LABEL[user.searcher_profile.fund_model]} · {SEARCH_STAGE_LABEL[user.searcher_profile.search_stage]}
            </span>
            <Link to="/searcher/onboarding" className="font-mono-label text-[#1E3A5F] underline underline-offset-4 ml-auto" data-testid="searcher-edit-profile-link">
              {t("Editar perfil")}
            </Link>
          </div>
        )}

        {welcome && (
          <div className="hairline p-6 bg-bone fade-up-delay-2 flex items-start gap-4" data-testid="searcher-ai-welcome">
            <div className="w-10 h-10 hairline flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <span className="font-mono-label text-gold">{t("Consejo de tu asesor AI · próximo paso")}</span>
              <p className="font-serif-display text-xl mt-1 leading-snug">{welcome}</p>
              {welcomeCta && (
                <Link to={welcomeCta.path} className="font-mono-label text-[#C25F1E] mt-3 inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="searcher-ai-cta">
                  {welcomeCta.label} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        <section data-testid="searcher-match-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="font-mono-label text-gold flex items-center gap-2">
                <Sparkles className="w-3 h-3" />{" "}{t("Motor de match triádico")}
              </span>
              <h2 className="font-serif-display text-3xl mt-1" data-testid="searcher-match-title">{t("Recomendadas para ti (IA Match)")}</h2>
            </div>
            {matches && <span className="font-mono-label text-muted-foreground">{matches.length}{" "}{t("analizados")}</span>}
          </div>
          {matches === null ? (
            <div className="hairline p-8 text-center bg-card" data-testid="searcher-match-loading">
              <span className="font-mono-label text-muted-foreground animate-pulse">{t("Calculando matches con IA…")}</span>
            </div>
          ) : matches.length === 0 ? (
            <div className="hairline p-8 text-center bg-card">
              <p className="text-muted-foreground text-sm">{t("Aún no hay empresas que analizar. Vuelve pronto.")}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 hairline border-r-0 border-b-0">
              {matches.slice(0, 3).map((m) => <MatchCard key={m.company.company_id} m={m} />)}
            </div>
          )}
        </section>

        <section data-testid="searcher-all-section">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif-display text-3xl" data-testid="searcher-all-title">{t("Todas las oportunidades")}</h2>
            <Link to="/searcher/pipeline" className="font-mono-label text-gold flex items-center gap-1" data-testid="searcher-go-pipeline">
              {t("Mi pipeline")}{" "}<ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {loading ? (
            <SkeletonGrid count={3} cols="md:grid-cols-2 lg:grid-cols-3" />
          ) : companies.length === 0 ? (
            <div className="hairline p-12 text-center bg-card">
              <Briefcase className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-serif-display text-2xl mb-2">{t("Aún no hay empresas cualificadas")}</h3>
              <p className="text-muted-foreground">{t("Vuelve pronto. Estamos cualificando nuevas oportunidades cada semana.")}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 hairline border-r-0 border-b-0">
              {companies.map((co) => (
                <CompanyTile key={co.company_id} co={co} />
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

const MatchCard = ({ m }) => (
  <Link
    to={`/searcher/company/${m.company.company_id}`}
    className="hairline border-l-0 border-t-0 p-7 bg-card hover:bg-bone transition-colors block group"
    data-testid={`searcher-match-card-${m.company.company_id}`}
  >
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        <span className="font-mono-label text-gold">{m.company.sector}</span>
        <h3 className="font-serif-display text-2xl leading-tight mt-1">{m.company.name_blind}</h3>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
          <MapPin className="w-3 h-3" /> {m.company.country}
        </div>
      </div>
      <MatchScore value={m.total_score} testid={`searcher-match-score-${m.company.company_id}`} />
    </div>
    <div className="mb-4">
      <BreakdownChips breakdown={m.hard_breakdown} max={SEARCHER_HARD_MAX} />
    </div>
    {m.soft_rationale && (
      <p className="text-sm text-muted-foreground italic line-clamp-3 mb-4 hairline-t pt-4">
        "{m.soft_rationale}"
      </p>
    )}
    <div className="flex items-center justify-between font-mono-label text-[#1E3A5F]">
      <span>{t("Ver teaser")}</span>
      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
    </div>
  </Link>
);

const CompanyTile = ({ co }) => {
  const facts = co.public_facts || {};
  const fin = co.financial_signal || {};
  // Take a 1-2 line summary from the teaser text
  const teaserSummary = (co.teaser_text || "")
    .replace(/^#{1,4}\s+[^\n]+\n*/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\n+/g, " ")
    .trim()
    .slice(0, 180);

  return (
    <Link
      to={`/searcher/company/${co.company_id}`}
      className="hairline border-l-0 border-t-0 p-7 bg-card hover:bg-bone transition-colors block group"
      data-testid={`searcher-tile-${co.company_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="font-mono-label text-gold">{t("Teaser ciego")}</span>
        {co.score != null && (
          <div className="hairline px-2 py-1 bg-bone text-right">
            <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{t("SCORE IA")}</div>
            <div className="font-serif-display text-lg leading-none text-gold">{co.score}</div>
          </div>
        )}
      </div>
      <h3 className="font-serif-display text-2xl leading-tight mb-2">{co.name_blind || co.sector}</h3>
      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-5">
        <MapPin className="w-3 h-3" /> {co.country}
      </div>

      {teaserSummary && (
        <p className="text-sm text-muted-foreground mb-5 line-clamp-3">{teaserSummary}…</p>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-3 hairline-t pt-4">
        <Stat label={t("Empleados")} value={facts.employees ? `${facts.employees}` : "—"} />
        <Stat label={t("Fundación")} value={facts.year_founded ? `${facts.year_founded}` : "—"} />
        <Stat label={t("Margen EBITDA")} value={fin.ebitda_margin_pct != null ? `${fin.ebitda_margin_pct}%` : "—"} />
        <Stat label={t("Crecimiento")} value={fin.growth_pct != null ? `${fin.growth_pct}%` : "—"} />
      </div>

      <div className="mt-5 flex items-center justify-between font-mono-label text-[#1E3A5F]">
        <span>{t("Ver teaser")}</span>
        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{label}</div>
    <div className="font-serif-display text-base">{value}</div>
  </div>
);

const formatN = (n) => {
  const num = Number(n);
  if (isNaN(num)) return n;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
  return num.toString();
};
